import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

interface ChatSidebarContextType {
  isOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  pendingContent: string | null;
  setPendingContent: (content: string | null) => void;
  clearPendingContent: () => void;
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
}

const ChatSidebarContext = createContext<ChatSidebarContextType | undefined>(undefined);

interface ChatSidebarProviderProps {
  children: React.ReactNode;
}

export const ChatSidebarProvider: React.FC<ChatSidebarProviderProps> = ({ children }) => {
  const isMobile = useIsMobile();
  
  // Initialize sidebar state - always default to open for desktop, closed for mobile
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      // Always default to open for desktop, closed for mobile (ignore localStorage for open state)
      return !isMobile;
    }
    return true; // Default fallback
  });
  
  const [pendingContent, setPendingContent] = useState<string | null>(null);
  
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('chatSidebarWidth');
      return saved ? parseInt(saved, 10) : 400;
    }
    return 400;
  });

  // Persist sidebar width to localStorage
  useEffect(() => {
    localStorage.setItem('chatSidebarWidth', sidebarWidth.toString());
  }, [sidebarWidth]);

  // Auto-close on mobile, auto-open on desktop (unless user has explicitly closed it)
  useEffect(() => {
    // Always open on desktop, close on mobile
    setIsOpen(!isMobile);
  }, [isMobile]);

  const toggleSidebar = useCallback(() => {
    // Don't allow toggling on mobile
    if (isMobile) return;
    setIsOpen(prev => !prev);
  }, [isMobile]);
  
  const openSidebar = useCallback(() => {
    // Don't allow opening on mobile
    if (isMobile) return;
    setIsOpen(true);
  }, [isMobile]);
  
  const closeSidebar = useCallback(() => {
    setIsOpen(false);
  }, []);
  
  const clearPendingContent = useCallback(() => {
    setPendingContent(null);
  }, []);

  const value = {
    isOpen,
    openSidebar,
    closeSidebar,
    toggleSidebar,
    pendingContent,
    setPendingContent,
    clearPendingContent,
    sidebarWidth,
    setSidebarWidth,
  };
  
  return (
    <ChatSidebarContext.Provider value={value}>
      {children}
    </ChatSidebarContext.Provider>
  );
};

export const useChatSidebar = (): ChatSidebarContextType => {
  const context = useContext(ChatSidebarContext);
  if (!context) {
    throw new Error('useChatSidebar must be used within a ChatSidebarProvider');
  }
  return context;
}; 